/**
 * Calculates the best time unit (day, week, month, year) based on the difference
 * between two dates.
 *
 * @param startDate - The start date of the range
 * @param endDate - The end date of the range
 * @returns One of 'day', 'week', 'month', or 'year' depending on the range
 */
export function getDateDiffUnit(
  startDate: Date,
  endDate: Date
): 'day' | 'week' | 'month' | 'year' {
  const diffInMs = endDate.getTime() - startDate.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInDays > 365) {
    return 'year';
  } else if (diffInDays > 90) {
    return 'month';
  } else if (diffInDays > 15) {
    return 'week';
  } else {
    return 'day';
  }
}

/**
 * Converts a JavaScript `Date` object into a string formatted as a SQL-compatible timestamp.
 *
 * Format: `YYYY-MM-DD HH:mm:ss` — commonly used in Java, SQL, and backend APIs.
 *
 * @param date - A valid JavaScript `Date` object.
 * @returns A string formatted as `YYYY-MM-DD HH:mm:ss`
 *
 * @example
 * const date = new Date('2024-12-31T23:59:59');
 * formatDateToSqlTimestamp(date); // "2024-12-31 23:59:59"
 */
export const formatDateToSqlTimestamp = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Parses a SQL-style timestamp (`YYYY-MM-DD HH:mm:ss`) back into a `Date`, as local time.
 *
 * The inverse of {@link formatDateToSqlTimestamp}. Passing that format straight to `new Date()` is
 * not reliable — the space-separated form is outside the ECMAScript spec and older Safari returns
 * an Invalid Date for it — so the separator is normalised to `T` first.
 *
 * @param value - A `YYYY-MM-DD HH:mm:ss` (or `YYYY-MM-DD HH:mm`) string.
 * @returns The corresponding `Date` in the local timezone.
 *
 * @example
 * parseSqlTimestamp('2024-12-31 23:59:59'); // Date for local Dec 31 2024, 23:59:59
 */
export const parseSqlTimestamp = (value: string): Date =>
  new Date(value.replace(' ', 'T'));

/**
 * Converts a JavaScript `Date` object into a string formatted to minute precision.
 *
 * Format: `YYYY-MM-DD HH:mm` — matches backend `@DateTimeFormat(pattern = "yyyy-MM-dd HH:mm")`
 * bindings (e.g. url-tracking analytics), which reject a seconds component as unparsed text.
 *
 * @param date - A valid JavaScript `Date` object.
 * @returns A string formatted as `YYYY-MM-DD HH:mm`
 *
 * @example
 * const date = new Date('2024-12-31T23:59:00');
 * formatDateToMinuteTimestamp(date); // "2024-12-31 23:59"
 */
export const formatDateToMinuteTimestamp = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * Sets the time of a given date to 23:59:00.
 *
 * @param date - The Date object to adjust.
 * @returns A new Date object with the time set to 23:59:00
 */
export const setEndOfDayTime = (date: Date): Date => {
  const adjusted = new Date(date);
  adjusted.setHours(23, 59, 59, 0);
  return adjusted;
};

/**
 * Sets the time of a given date to 00:00:00.
 *
 * @param date - The Date object to adjust.
 * @returns A new Date object with the time set to 00:00:00
 */
export const setStartOfDayTime = (date: Date): Date => {
  const adjusted = new Date(date);
  adjusted.setHours(0, 0, 0, 0);
  return adjusted;
};
